// src/pages/supervisor/ResearchPerformanceMetrics.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    Box,
    Grid,
    Card,
    CardContent,
    useTheme,
    Tooltip,
    Alert,
    CircularProgress,
    LinearProgress,
    Chip,
    Avatar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Divider,
    Stack,
    Skeleton,
    Rating,
    IconButton,
    CardActionArea,
    useMediaQuery
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    TrendingUp,
    TrendingDown,
    TrendingFlat,
    Star,
    ThumbUp,
    CheckCircle,
    Warning,
    PriorityHigh,
    School,
    Book,
    EmojiEvents,
    Assessment,
    Timeline,
    BarChart,
    People,
    Article,
    Work,
    Info,
    Psychology,
    ShowChart,
    Equalizer,
    PieChart
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIResearchPerformanceMetrics,
    APIResearchPerformanceStats
} from '../../apiServices/APIs';
import { 
    getUserDetails
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
import SupervisorBar from '../../components/SupervisorBar';
import authService from '../../apiServices/authService';
import { FullWidthContainer } from '../../components/FullWidthContainer';

const ResearchPerformanceMetrics = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [metrics, setMetrics] = useState(null);
    const [historicalData, setHistoricalData] = useState([]);
    const [stats, setStats] = useState(null);
    const [trend, setTrend] = useState(null);
    const [activeMetricTab, setActiveMetricTab] = useState('overview');
    const showToast = useCustomToast();
    const isMounted = useRef(true);
    const supervisorId = getUserDetails("Id");

    // Handle logout
    const handleLogout = async () => {
        try {
            await authService.logout();
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchAllData();
        
        return () => {
            isMounted.current = false;
        };
    }, []);

    const fetchAllData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            await Promise.all([
                fetchMetrics(),
                fetchStats()
            ]);
        } catch (err) {
            setError("Failed to load performance data");
            console.error("Fetch error:", err);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const fetchMetrics = async () => {
        const result = await apiService.listById(APIResearchPerformanceMetrics, supervisorId);
        if (result.success && result.data && isMounted.current) {
            setMetrics(result.data.current);
            setHistoricalData(result.data.historical || []);
            setTrend(result.data.trend);
        }
    };

    const fetchStats = async () => {
        const result = await apiService.listById(APIResearchPerformanceStats, supervisorId);
        if (result.success && result.data && isMounted.current) {
            setStats(result.data);
        }
    };

    const getRatingIcon = (rating) => {
        switch(rating) {
            case 'excellent': return <Star sx={{ color: '#FFD700' }} />;
            case 'good': return <ThumbUp sx={{ color: '#4CAF50' }} />;
            case 'satisfactory': return <CheckCircle sx={{ color: '#2196F3' }} />;
            case 'needs_improvement': return <Warning sx={{ color: '#FF9800' }} />;
            case 'critical': return <PriorityHigh sx={{ color: '#F44336' }} />;
            default: return <Info />;
        }
    };

    const getRatingColor = (rating) => {
        switch(rating) {
            case 'excellent': return 'success';
            case 'good': return 'info';
            case 'satisfactory': return 'warning';
            case 'needs_improvement': return 'warning';
            case 'critical': return 'error';
            default: return 'default';
        }
    };

    const getTrendIcon = (direction) => {
        switch(direction) {
            case 'improving': return <TrendingUp sx={{ color: '#4CAF50' }} />;
            case 'declining': return <TrendingDown sx={{ color: '#F44336' }} />;
            default: return <TrendingFlat sx={{ color: '#FF9800' }} />;
        }
    };

    // Custom progress bar component
    const MetricProgressBar = ({ value, label, maxValue = 100, color = 'primary' }) => (
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                    {label}
                </Typography>
                <Typography variant="caption" fontWeight="bold" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                    {value}{maxValue === 100 ? '%' : ''}
                </Typography>
            </Box>
            <LinearProgress 
                variant="determinate" 
                value={Math.min(100, (value / maxValue) * 100)} 
                color={color}
                sx={{ height: 8, borderRadius: 4 }}
            />
        </Box>
    );

    // Metric Card component
    const MetricCard = ({ title, value, icon, color, subtitle, trend }) => (
        <Card sx={{ 
            height: '100%', 
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': { 
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[8]
            }
        }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    mb: 1,
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 0 }
                }}>
                    <Avatar sx={{ bgcolor: `${color}.light`, color: `${color}.main` }}>
                        {icon}
                    </Avatar>
                    {trend && (
                        <Chip 
                            size="small" 
                            icon={getTrendIcon(trend.direction)} 
                            label={`${trend.value}%`} 
                            color={trend.direction === 'improving' ? 'success' : trend.direction === 'declining' ? 'error' : 'warning'} 
                        />
                    )}
                </Box>
                <Typography variant="h4" sx={{ 
                    fontWeight: 'bold', 
                    mb: 0.5,
                    fontSize: { xs: '1.5rem', sm: '2rem' }
                }}>
                    {value}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {title}
                </Typography>
                {subtitle && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                        {subtitle}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );

    if (loading) {
        return <LoadingState message="Loading Performance Metrics..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return (
            <ErrorState 
                error={error} 
                onRetry={fetchAllData} 
                title="Unable to Load Performance Metrics" 
                variant="research" 
                fullPage 
                retryText="Retry Loading" 
            />
        );
    }

    if (!metrics) {
        return (
            <>
                <Container>
                    <Box sx={{ backgroundColor: 'white', borderRadius: 3 }}>
                        <SupervisorBar notificationCount={3} />
                        <Paper sx={{ 
                            p: { xs: 4, sm: 6 }, 
                            textAlign: 'center', 
                            borderRadius: 3,
                            backgroundColor: 'white'
                        }}>
                            <Assessment sx={{ fontSize: { xs: 48, sm: 64 }, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h5" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                                No Performance Data Available
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Performance metrics will appear once you start supervising students.
                            </Typography>
                            <Button 
                                variant="contained" 
                                color="primary" 
                                onClick={fetchAllData} 
                                startIcon={<RefreshIcon />}
                                size={isMobile ? "small" : "medium"}
                            >
                                Refresh Data
                            </Button>
                        </Paper>
                    </Box>
                </Container>
            </>
        );
    }

    return (
        <>
            <FullWidthContainer>
                <Box sx={{ backgroundColor: 'white', borderRadius: 3 }}>
                    <SupervisorBar notificationCount={3} />
                    {/* Header Section */}
                    <Paper elevation={2} sx={{ 
                        p: { xs: 2, sm: 3 }, 
                        mb: { xs: 2, sm: 3 }, 
                        borderRadius: 3,
                        backgroundColor: 'white',
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            flexWrap: 'wrap', 
                            gap: 2,
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                            <Typography variant="h6" sx={{ 
                                fontWeight: 'bold', 
                                color: theme.palette.primary.main, 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                fontSize: { xs: '1rem', sm: '1.25rem' },
                                flexWrap: 'wrap',
                                justifyContent: { xs: 'center', sm: 'flex-start' }
                            }}>
                                <Assessment /> Research Performance Metrics
                                <Chip 
                                    size="small" 
                                    icon={getRatingIcon(metrics.performance_rating)}
                                    label={metrics.formatted_performance_rating}
                                    color={getRatingColor(metrics.performance_rating)}
                                    sx={{ ml: { xs: 0, sm: 1 }, mt: { xs: 1, sm: 0 } }}
                                />
                            </Typography>
                            <Button 
                                variant="outlined" 
                                color="info" 
                                startIcon={<RefreshIcon />} 
                                onClick={fetchAllData} 
                                sx={{ 
                                    textTransform: 'none',
                                    width: { xs: '100%', sm: 'auto' }
                                }}
                                size={isMobile ? "small" : "medium"}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </Paper>
                </Box>

                {/* Stats Cards */}
                {stats && (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6} sm={3}>
                            <MetricCard 
                                title="Average Score" 
                                value={`${stats.average_score}%`} 
                                icon={<Equalizer />} 
                                color="primary"
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <MetricCard 
                                title="Excellent Periods" 
                                value={stats.excellent_periods} 
                                icon={<Star />} 
                                color="warning"
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <MetricCard 
                                title="Graduates" 
                                value={stats.total_graduates} 
                                icon={<School />} 
                                color="success"
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <MetricCard 
                                title="Publications" 
                                value={stats.total_publications} 
                                icon={<Book />} 
                                color="info"
                            />
                        </Grid>
                    </Grid>
                )}

                {/* Current Period Overview */}
                <Paper elevation={2} sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    mb: { xs: 2, sm: 3 }, 
                    borderRadius: 3,
                    backgroundColor: 'white',
                }}>
                    <Typography variant="h6" sx={{ 
                        fontWeight: 'bold', 
                        mb: 2, 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}>
                        <Timeline /> Current Period: {metrics.formatted_semester} {metrics.academic_year}
                    </Typography>
                    
                    <Grid container spacing={3}>
                        {/* Overall Score Gauge */}
                        <Grid item xs={12} md={4}>
                            <Box sx={{ textAlign: 'center', p: { xs: 1, sm: 2 } }}>
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                    <CircularProgress 
                                        variant="determinate" 
                                        value={metrics.overall_score} 
                                        size={isMobile ? 140 : 180} 
                                        thickness={8}
                                        sx={{ color: theme.palette[getRatingColor(metrics.performance_rating)].main }}
                                    />
                                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                                        <Typography variant="h3" sx={{ 
                                            fontWeight: 'bold',
                                            fontSize: { xs: '1.5rem', sm: '2rem' }
                                        }}>
                                            {metrics.overall_score}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                            Overall Score
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ mt: 2 }}>
                                    <Chip 
                                        icon={getRatingIcon(metrics.performance_rating)} 
                                        label={metrics.formatted_performance_rating} 
                                        color={getRatingColor(metrics.performance_rating)} 
                                        size={isMobile ? "small" : "medium"}
                                    />
                                    <Chip 
                                        icon={getTrendIcon(metrics.trend_direction)} 
                                        label={metrics.formatted_trend_direction} 
                                        color={metrics.trend_direction === 'improving' ? 'success' : metrics.trend_direction === 'declining' ? 'error' : 'warning'} 
                                        size={isMobile ? "small" : "medium"}
                                        sx={{ ml: 1 }} 
                                    />
                                </Box>
                                {trend && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                        {trend.message}
                                    </Typography>
                                )}
                            </Box>
                        </Grid>

                        {/* Key Metrics Grid */}
                        <Grid item xs={12} md={8}>
                            <Grid container spacing={2}>
                                <Grid item xs={6} sm={4}>
                                    <MetricCard 
                                        title="Graduated Students" 
                                        value={metrics.metrics.students_graduated} 
                                        icon={<School />} 
                                        color="primary" 
                                    />
                                </Grid>
                                <Grid item xs={6} sm={4}>
                                    <MetricCard 
                                        title="Publications" 
                                        value={metrics.metrics.publications} 
                                        icon={<Book />} 
                                        color="secondary" 
                                    />
                                </Grid>
                                <Grid item xs={6} sm={4}>
                                    <MetricCard 
                                        title="Retention Rate" 
                                        value={`${metrics.metrics.retention_rate}%`} 
                                        icon={<People />} 
                                        color="info" 
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ 
                                        p: { xs: 1.5, sm: 2 }, 
                                        textAlign: 'center',
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 2,
                                        height: '100%'
                                    }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            Student Satisfaction
                                        </Typography>
                                        <Rating 
                                            value={metrics.metrics.student_satisfaction} 
                                            precision={0.1} 
                                            readOnly 
                                            size={isMobile ? "medium" : "large"} 
                                            sx={{ my: 1 }} 
                                        />
                                        <Typography variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                            ({metrics.metrics.student_satisfaction}/5)
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <MetricCard 
                                        title="Funding Proposals" 
                                        value={metrics.metrics.funding_proposals} 
                                        icon={<Work />} 
                                        color="success" 
                                    />
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <MetricCard 
                                        title="Student Awards" 
                                        value={metrics.metrics.student_awards} 
                                        icon={<EmojiEvents />} 
                                        color="warning" 
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Performance Metrics Detailed */}
                <Paper elevation={2} sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    mb: { xs: 2, sm: 3 }, 
                    borderRadius: 3,
                    backgroundColor: 'white',
                }}>
                    <Typography variant="h6" sx={{ 
                        fontWeight: 'bold', 
                        mb: 3, 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}>
                        <ShowChart /> Performance Breakdown
                    </Typography>
                    
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" sx={{ 
                                fontWeight: 'bold', 
                                mb: 2,
                                fontSize: { xs: '0.875rem', sm: '1rem' }
                            }}>
                                Your Performance vs Department Average
                            </Typography>
                            <Box sx={{ mb: 3 }}>
                                <MetricProgressBar 
                                    label="Graduation Rate" 
                                    value={Math.min(100, (metrics.metrics.students_graduated / 20) * 100)} 
                                    color="primary"
                                />
                                <MetricProgressBar 
                                    label="Publications" 
                                    value={Math.min(100, (metrics.metrics.publications / 10) * 100)} 
                                    color="secondary"
                                />
                                <MetricProgressBar 
                                    label="Retention Rate" 
                                    value={metrics.metrics.retention_rate} 
                                    color="info"
                                />
                                <MetricProgressBar 
                                    label="Student Satisfaction" 
                                    value={(metrics.metrics.student_satisfaction / 5) * 100} 
                                    color="warning"
                                />
                                <MetricProgressBar 
                                    label="Funding Success" 
                                    value={Math.min(100, (metrics.metrics.funding_proposals / 5) * 100)} 
                                    color="success"
                                />
                                <MetricProgressBar 
                                    label="Awards Received" 
                                    value={Math.min(100, (metrics.metrics.student_awards / 5) * 100)} 
                                    color="error"
                                />
                            </Box>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" sx={{ 
                                fontWeight: 'bold', 
                                mb: 2,
                                fontSize: { xs: '0.875rem', sm: '1rem' }
                            }}>
                                Department Comparison
                            </Typography>
                            <Box sx={{ mb: 3 }}>
                                {metrics.department_averages && (
                                    <>
                                        <MetricProgressBar 
                                            label="Department Graduation Avg" 
                                            value={Math.min(100, (metrics.department_averages.students_graduated / 20) * 100)} 
                                            color="grey"
                                        />
                                        <MetricProgressBar 
                                            label="Department Publications Avg" 
                                            value={Math.min(100, (metrics.department_averages.publications / 10) * 100)} 
                                            color="grey"
                                        />
                                        <MetricProgressBar 
                                            label="Department Retention Avg" 
                                            value={metrics.department_averages.retention_rate} 
                                            color="grey"
                                        />
                                        <MetricProgressBar 
                                            label="Department Satisfaction Avg" 
                                            value={(metrics.department_averages.student_satisfaction / 5) * 100} 
                                            color="grey"
                                        />
                                    </>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Strengths & Improvements */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ 
                            p: { xs: 2, sm: 3 }, 
                            borderRadius: 3, 
                            height: '100%', 
                            bgcolor: theme.palette.success.light + '15',
                            backgroundColor: 'white',
                            borderLeft: `4px solid ${theme.palette.success.main}`
                        }}>
                            <Typography variant="h6" sx={{ 
                                fontWeight: 'bold', 
                                mb: 2, 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}>
                                <CheckCircle color="success" /> Strength Areas
                            </Typography>
                            <Stack spacing={1.5}>
                                {metrics.strength_areas?.map((area, idx) => (
                                    <Card key={idx} variant="outlined" sx={{ bgcolor: 'transparent' }}>
                                        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CheckCircle color="success" fontSize="small" />
                                                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                    {area}
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ 
                            p: { xs: 2, sm: 3 }, 
                            borderRadius: 3, 
                            height: '100%', 
                            bgcolor: theme.palette.warning.light + '15',
                            backgroundColor: 'white',
                            borderLeft: `4px solid ${theme.palette.warning.main}`
                        }}>
                            <Typography variant="h6" sx={{ 
                                fontWeight: 'bold', 
                                mb: 2, 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}>
                                <Warning color="warning" /> Improvement Areas
                            </Typography>
                            <Stack spacing={1.5}>
                                {metrics.improvement_areas?.map((area, idx) => (
                                    <Card key={idx} variant="outlined" sx={{ bgcolor: 'transparent' }}>
                                        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Warning color="warning" fontSize="small" />
                                                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                    {area}
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Goal Progress Section */}
                <Paper sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    borderRadius: 3, 
                    mb: 3,
                    backgroundColor: 'white'
                }}>
                    <Typography variant="h6" sx={{ 
                        fontWeight: 'bold', 
                        mb: 3, 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}>
                        <BarChart color="primary" /> Annual Goals Progress
                    </Typography>
                    
                    {metrics.goal_progress?.length > 0 ? (
                        <Grid container spacing={2}>
                            {metrics.goal_progress.map((goal, idx) => (
                                <Grid item xs={12} md={6} key={idx}>
                                    <Card variant="outlined">
                                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                            <Box sx={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                alignItems: 'center', 
                                                mb: 1,
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                gap: { xs: 1, sm: 0 }
                                            }}>
                                                <Typography variant="subtitle2" sx={{ 
                                                    fontWeight: 'bold',
                                                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                                }}>
                                                    {goal.goal}
                                                </Typography>
                                                <Chip 
                                                    size="small" 
                                                    label={goal.status.replace('_', ' ').toUpperCase()} 
                                                    color={goal.status === 'completed' ? 'success' : goal.status === 'on_track' ? 'info' : goal.status === 'behind' ? 'warning' : 'error'}
                                                />
                                            </Box>
                                            <MetricProgressBar 
                                                label={`${goal.current} / ${goal.target}`} 
                                                value={goal.percentage} 
                                                color={goal.status === 'completed' ? 'success' : goal.status === 'on_track' ? 'info' : 'warning'}
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: { xs: 3, sm: 4 } }}>
                            <Info sx={{ fontSize: { xs: 36, sm: 48 }, color: 'text.secondary', mb: 2 }} />
                            <Typography color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                No goals set for this period
                            </Typography>
                        </Box>
                    )}
                </Paper>

                {/* Historical Data Table */}
                {historicalData.length > 0 && (
                    <Paper sx={{ 
                        p: { xs: 2, sm: 3 }, 
                        borderRadius: 3,
                        backgroundColor: 'white',
                        overflow: 'auto'
                    }}>
                        <Typography variant="h6" sx={{ 
                            fontWeight: 'bold', 
                            mb: 2, 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            fontSize: { xs: '1rem', sm: '1.25rem' }
                        }}>
                            <Timeline /> Historical Performance
                        </Typography>
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table size="small" sx={{ minWidth: { xs: 500, sm: 'auto' } }}>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                                        <TableCell><strong>Period</strong></TableCell>
                                        <TableCell align="center"><strong>Score</strong></TableCell>
                                        <TableCell align="center"><strong>Rating</strong></TableCell>
                                        <TableCell align="center"><strong>Graduates</strong></TableCell>
                                        <TableCell align="center"><strong>Publications</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {historicalData.map((item) => (
                                        <TableRow key={item.id} hover>
                                            <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                {item.period}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip 
                                                    label={`${item.overall_score}%`} 
                                                    size="small" 
                                                    color={item.rating_color} 
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip 
                                                    icon={getRatingIcon(item.performance_rating)} 
                                                    label={item.formatted_rating} 
                                                    size="small" 
                                                    color={item.rating_color} 
                                                />
                                            </TableCell>
                                            <TableCell align="center">{item.students_graduated}</TableCell>
                                            <TableCell align="center">{item.publications}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}

                {/* Last Updated */}
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                        Last updated: {new Date(metrics.last_updated).toLocaleString()}
                    </Typography>
                </Box>
            </FullWidthContainer>
        </>
    );
};

export default ResearchPerformanceMetrics;